// src/redux/sagas/restaurantSaga.ts

import { call, put, takeEvery } from "redux-saga/effects";
import { AxiosResponse } from "axios";
import { request } from "../../api/request";
import { determineRestaurantApiCall } from "../../utils/restaurantApiRouter";
import {
  fetchRestaurantsRequest,
  fetchRestaurantsSuccess,
  fetchRestaurantsFailure,
  selectRestaurant,
} from "../slices/restaurantSlice";
import { checkAndValidateEditControl } from "../../utils/editControlUtils";
import { fetchSiteContentRequest } from "../slices/siteContentSlice";
import { shouldSkipApiCalls } from "../../utils/appConfig";
import {
  transformFranchisesToRestaurants,
  extractFranchisesFromRestaurant,
} from "../../utils/franchiseTransformer";

function* fetchRestaurantsSaga(): Generator<unknown, void, AxiosResponse<any>> {
  try {
    // Skip API calls for template preview mode
    if (shouldSkipApiCalls()) {
      return;
    }

    // Determine API
    const apiConfig = determineRestaurantApiCall();

    // Fetch restaurants
    const response = yield call(request<any>, {
      method: "GET",
      url: apiConfig.url,
      requiresRestaurantId: false,
      headers: apiConfig.headers,
    });

    // Parent Restaurant ID
    let parentRestaurantId: string | null = null;

    if (
      apiConfig.fetchType === "parent" &&
      apiConfig.headers?.["parent-restaurant-id"]
    ) {
      parentRestaurantId = apiConfig.headers["parent-restaurant-id"];
    }

    // Extract franchise data
    const franchiseData = response?.data?.data;

    const franchiseList = extractFranchisesFromRestaurant(franchiseData);

    const restaurantsList = transformFranchisesToRestaurants(franchiseList);

    // Save restaurants in Redux
    yield put(
      fetchRestaurantsSuccess({
        restaurants: restaurantsList,
        fetchType: apiConfig.fetchType,
        parentRestaurantId,
      }),
    );

    // =====================================================
    // Auto select when only one restaurant
    // =====================================================

    if (restaurantsList.length === 1) {
      const restaurant = restaurantsList[0];

      if (restaurant?.restaurant_id) {
        yield put(selectRestaurant(restaurant.restaurant_id));

        yield put(fetchSiteContentRequest());

        checkAndValidateEditControl(restaurant.restaurant_id);
      }
    }
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : "Failed to fetch restaurants";

    yield put(fetchRestaurantsFailure(errorMessage));
  }
}

export function* watchRestaurantSaga() {
  yield takeEvery(fetchRestaurantsRequest.type, fetchRestaurantsSaga);
}
